/**
 * Shareable doors for one sign-in flow.
 *
 * `/login?flow=<id>` opens the sign-in form on that flow.
 * `/home?flow=<id>` (or the student home, when that is where the flow lands)
 * opens the workspace as that role. The id has to be a flow this browser
 * already has. An unknown id is ignored so a crafted query cannot invent a role.
 * A flow that still asks a question stays on the login link.
 */

import { signIn, isSignedIn } from "@/lib/auth-session"
import {
  applyLoginSession,
  flowSession,
  getLoginFlows,
  type LoginFlowDefinition,
} from "@/lib/login-flow"
import { getLoginSession } from "@/lib/login-session"
import { PRODUCTS_HOME_PATH } from "@/lib/post-auth-landing"
import { DEMO_FLOW_PROFILES } from "@/lib/workspace-session-user"

export function signInRolePath(flowId: string): string {
  return `/login?flow=${encodeURIComponent(flowId)}`
}

export function requestedSignInFlowId(flowParam: string | null | undefined): string | null {
  const id = flowParam?.trim()
  if (!id) return null
  return getLoginFlows().some(flow => flow.id === id) ? id : null
}

export function flowNeedsSignInChoices(flow: LoginFlowDefinition): boolean {
  return flow.steps.some(step => step.kind === "choice")
}

export function signInRoleLandingPath(flow: LoginFlowDefinition): string {
  const landing = flowSession(flow).landing.trim()
  return landing.startsWith("/") ? landing : PRODUCTS_HOME_PATH
}

export function withSignInRoleQuery(path: string, flowId: string): string {
  const url = new URL(path, "http://local.invalid")
  url.searchParams.set("flow", flowId)
  return `${url.pathname}${url.search}${url.hash}`
}

/** Home (or student home) address for a role that does not ask a question. */
export function signInRoleHomePath(flowId: string): string | null {
  const flow = getLoginFlows().find(entry => entry.id === flowId)
  if (!flow || flowNeedsSignInChoices(flow)) return null
  return withSignInRoleQuery(signInRoleLandingPath(flow), flow.id)
}

/** Menu and pasted links: home when the role is fully known, otherwise login. */
export function signInRoleHref(flowId: string): string {
  return signInRoleHomePath(flowId) ?? signInRolePath(flowId)
}

/**
 * Apply a direct role from a home link.
 *
 * Returns the address to show, or `null` when the id is unknown or the flow
 * still has a question.
 */
export function openSignInRoleFromLink(flowId: string): string | null {
  const flow = getLoginFlows().find(entry => entry.id === flowId)
  if (!flow || flowNeedsSignInChoices(flow)) return null
  const landing = signInRoleLandingPath(flow)
  const destination = withSignInRoleQuery(landing, flow.id)
  if (isSignedIn() && getLoginSession().demoFlowId === flow.id) return destination
  signIn(DEMO_FLOW_PROFILES[flow.id]?.email ?? "")
  applyLoginSession(flow, null, landing)
  return destination
}
